/*
 * Host-aware routing for the VoxPont marketing pages.
 *
 * On the public marketing host (voxpont.com) the marketing hub is served at the site root ("/")
 * and each domain variant gets a clean top-level path ("/medical", "/pharmacy", ...). Everywhere else
 * (the app domains, *.web.app, localhost) the same pages live under "/voxpontmarketing" so they
 * don't shadow the application's own root redirect.
 *
 * All hosting targets serve the same SPA build, so the only thing that differs between
 * voxpont.com and the app domains is this client-side host check.
 */

// Slugs that get a clean top-level route on the marketing host. Must match the VARIANTS in
// voxpontMarketingData.js. Chosen to not collide with any application route. Kept as a literal
// (rather than imported from the data module) so it stays in the main bundle while the marketing
// page itself remains lazy-loaded.
export const MARKETING_SLUGS = [
  "medical",
  "education",
  "crisis",
  "pharmacy",
  "kiosk",
  "social",
  "lowbandwidth",
  "offline",
];

// Canonical base path for the marketing pages on the application domains.
const MARKETING_BASE = "/voxpont";

/**
 * True when the current host should serve the marketing site at the root.
 * Matches voxpont.com and any subdomain (www.voxpont.com, etc.). A localStorage flag
 * ("VOXPONT_MARKETING_HOST" = "1") lets you preview the root behavior locally.
 */
export function isVoxPontMarketingHost() {
  if (typeof window === "undefined") return false;
  try {
    if (window.localStorage?.getItem("VOXPONT_MARKETING_HOST") === "1") return true;
  } catch {
    // localStorage may be unavailable (private mode); fall through to host check.
  }
  const host = window.location.hostname.toLowerCase();
  return host === "voxpont.com" || host.endsWith(".voxpont.com");
}

/**
 * True when the current host is a VoxPont application subdomain (e.g. app.voxpont.com).
 * Companion to isVoxPontMarketingHost(): it intentionally matches the same family of
 * hosts, because app.voxpont.com serves both the marketing splash (at "/") and the app
 * routes (/auth, /voxpont12, ...) from the same React build. Used to render the branded
 * VoxPontAuthPage at /auth on VoxPont domains while HealthDesk keeps its own AuthPage.
 */
export function isVoxPontAppDomain() {
  return (
    typeof window !== "undefined" &&
    window.location.hostname.endsWith(".voxpont.com")
  );
}

/** Path of the marketing hub for the current host. */
export function marketingHubPath() {
  return isVoxPontMarketingHost() ? "/" : MARKETING_BASE;
}

/** Path of a marketing variant page for the current host. */
export function marketingVariantPath(slug) {
  return isVoxPontMarketingHost() ? `/${slug}` : `${MARKETING_BASE}/${slug}`;
}

/**
 * True when a path is intrinsically a VoxPont page on ANY host: the branded
 * auth / onboarding / beta / admin routes, the marketing splash + brand board,
 * and every VoxPont product fork ("/voxpont2" … "/voxpont100"). Used to scope
 * the VoxPont favicon + social branding to VoxPont surfaces only.
 */
export function isVoxPontBrandedPath(pathname = "") {
  if (typeof pathname !== "string") return false;
  if (
    pathname === "/voxpont-auth" ||
    pathname === "/voxpont-start" ||
    pathname === "/voxpont-beta-join" ||
    pathname === "/voxpont-terms" ||
    pathname === "/voxpont-privacy" ||
    pathname === "/voxpont-baa" ||
    pathname === "/admin/voxpont-beta"
  ) {
    return true;
  }
  // Marketing splash, per-domain variants, and the brand board ("/voxpont",
  // "/voxpont/<slug>", "/voxpont/brand") + the legacy "/voxpontmarketing" paths.
  if (
    pathname === "/voxpont" ||
    pathname.startsWith("/voxpont/") ||
    pathname === "/voxpontmarketing" ||
    pathname.startsWith("/voxpontmarketing/")
  ) {
    return true;
  }
  // VoxPont product forks: "/voxpont2" … "/voxpont100".
  return /^\/voxpont\d+$/.test(pathname);
}

/**
 * True when the VoxPont favicon/social branding should apply for the given path.
 * The whole voxpont.com host family is VoxPont (every page, including "/" and
 * "/auth"); on other hosts only the explicit VoxPont routes qualify.
 */
export function shouldApplyVoxPontBranding(pathname = "") {
  return (
    isVoxPontMarketingHost() ||
    isVoxPontAppDomain() ||
    isVoxPontBrandedPath(pathname)
  );
}
